import React from 'react';
import SortableHeader from './SortableHeader';
import MgmtFeeWarningToggle from './MgmtFeeWarningToggle';
import { MarkReviewedButton, UndoReviewButton } from './ReviewActions';
import {
  ACCOUNT_SORT_ACCESSORS,
  REASON_LABELS,
  alertTitle,
  fmtCurrency,
  fmtCurrencyPrecise,
  sortByColumn,
} from './helpers';

const AccountStatusCell = ({ account }) => {
  if (account.review_status === 'reviewed') {
    return (
      <span className="badge bg-success" title={`Reviewed by ${account.last_reviewed_by || 'unknown'}`}>
        <i className="bi bi-check2-circle me-1"></i>
        Reviewed
      </span>
    );
  }
  if (account.alert_level === 'red') {
    return (
      <span className="badge bg-danger" title={alertTitle(account)}>
        <i className="bi bi-exclamation-triangle-fill me-1"></i>
        Needs Attention
        {account.alert_counts?.red > 1 ? ` (${account.alert_counts.red})` : ''}
      </span>
    );
  }
  if (account.review_status === 'needs_rereview') {
    return (
      <span
        className="badge bg-warning text-dark"
        title={`Re-review: ${(account.rereview_reasons || []).map((r) => REASON_LABELS[r] || r).join(', ')}`}
      >
        <i className="bi bi-arrow-repeat me-1"></i>
        Re-review
      </span>
    );
  }
  if (account.alert_level === 'orange') {
    return (
      <span className="badge bg-warning text-dark" title={alertTitle(account)}>
        <i className="bi bi-lightbulb me-1"></i>
        Suggested ({account.alert_counts?.orange || 0})
      </span>
    );
  }
  if (account.recent_transaction_count > 0) {
    return <span className="text-muted small">Inflows only ({account.recent_transaction_count})</span>;
  }
  return <span className="text-muted small">No recent activity</span>;
};

/**
 * The family's accounts, and the only home for per-account actions.
 *
 * Reviews and the management-fee waiver are keyed by account number on the
 * backend and have no family equivalent, so they belong on the row rather than
 * in the family header. Rendered even for a single account: that keeps the
 * ungrouped view on exactly the same component and stops the two views drifting.
 *
 * When `selectable`, this is also the selector for the sections below it — so
 * it always lists the whole family and is never itself filtered.
 */
const AccountsSubtable = ({
  accounts,
  days,
  sortConfig,
  onSort,
  onReviewed,
  onIgnored,
  matchesFilters,
  selectable = false,
  selected,
  onToggleAccount,
  onToggleAll,
  headerActions,
}) => {
  const showRmd = accounts.some((a) => a.rmd_amount != null);
  // every(), not selected.size === accounts.length: a stale account number left
  // over from a reload would pad the size and make the header checkbox lie.
  const allSelected =
    selectable && accounts.length > 0 && accounts.every((a) => selected.has(a.account_number));

  return (
    <div className="mb-3">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <div className="text-muted small text-uppercase" style={{ letterSpacing: '0.05em' }}>
          Accounts
        </div>
        {headerActions}
      </div>
      <div className="table-responsive">
        <table className="table table-sm align-middle mb-0 bg-white">
          <thead className="table-light">
            <tr>
              {selectable && (
                // Fixed width or the checkbox floats in a proportional column.
                <th scope="col" className="text-center" style={{ width: '2.5rem' }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={allSelected}
                    onChange={onToggleAll}
                    aria-label="Select all accounts"
                    title="Select accounts to filter the sections below"
                  />
                </th>
              )}
              <SortableHeader label="Account Name" sortKey="account_name" sortConfig={sortConfig} onSort={onSort} />
              <SortableHeader label="Account #" sortKey="account_number" sortConfig={sortConfig} onSort={onSort} />
              <SortableHeader label="Type" sortKey="account_type" sortConfig={sortConfig} onSort={onSort} />
              <SortableHeader label="Status" sortKey="status" sortConfig={sortConfig} onSort={onSort} />
              <SortableHeader label="Reserve Target" sortKey="total_reserve_dollar" sortConfig={sortConfig} onSort={onSort} className="text-end" />
              <SortableHeader label="Total Cash" sortKey="total_cash_dollar" sortConfig={sortConfig} onSort={onSort} className="text-end" />
              <SortableHeader label={`Outflows (${days}d)`} sortKey="recent_outflows" sortConfig={sortConfig} onSort={onSort} className="text-end" />
              <SortableHeader label="Account Value" sortKey="account_current_value" sortConfig={sortConfig} onSort={onSort} className="text-end" />
              {showRmd && (
                <SortableHeader label="RMD" sortKey="rmd_amount" sortConfig={sortConfig} onSort={onSort} className="text-end" />
              )}
              <th scope="col" className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((account) => {
              // A family matches the filters as a whole, so it can contain
              // accounts the reviewer filtered out. Dim them rather than hide
              // them — they're still part of the family's totals.
              const dimmed = matchesFilters && !matchesFilters(account);
              const rmdTitle = [
                /^(true|yes|1)$/i.test(String(account.rmd_annual ?? '')) ? 'Annual' : null,
                account.rmd_10_year_rule ? `10-year rule: ${account.rmd_10_year_rule}` : null,
              ]
                .filter(Boolean)
                .join(' · ');
              const isSelected = selectable && selected.has(account.account_number);
              return (
                <tr
                  key={account.account_number}
                  className={isSelected ? 'table-primary' : undefined}
                  style={dimmed ? { opacity: 0.55 } : undefined}
                >
                  {selectable && (
                    <td className="text-center">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={isSelected}
                        onChange={() => onToggleAccount(account.account_number)}
                        aria-label={`Show only ${account.account_name || account.account_number}`}
                      />
                    </td>
                  )}
                  <td className="fw-semibold">
                    {account.account_name || 'Unknown'}
                    {account.has_potential_duplicates && (
                      <i
                        className="bi bi-exclamation-diamond-fill text-warning ms-2"
                        title="Has potential duplicate reserves"
                      ></i>
                    )}
                  </td>
                  <td className="text-muted">{account.account_number}</td>
                  <td>{account.account_type || '—'}</td>
                  <td><AccountStatusCell account={account} /></td>
                  <td className="text-end">{fmtCurrency(account.total_reserve_dollar)}</td>
                  <td className="text-end">{fmtCurrency(account.total_cash_dollar)}</td>
                  <td className="text-end">
                    {account.recent_outflows > 0 ? (
                      <span className="text-danger fw-semibold">{fmtCurrency(account.recent_outflows)}</span>
                    ) : (
                      <span className="text-muted">{fmtCurrency(0)}</span>
                    )}
                  </td>
                  <td className="text-end">{fmtCurrency(account.account_current_value)}</td>
                  {showRmd && (
                    <td className="text-end" title={rmdTitle || undefined}>
                      {account.rmd_amount != null ? (
                        <>
                          {fmtCurrencyPrecise(account.rmd_amount)}
                          {rmdTitle && <i className="bi bi-info-circle text-muted ms-1"></i>}
                        </>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  )}
                  <td>
                    <div className="d-flex align-items-center justify-content-end gap-2 flex-wrap">
                      <MgmtFeeWarningToggle account={account} onChanged={onIgnored} compact />
                      {/* One review action at a time: an up-to-date review
                          offers only Undo; needs_rereview keeps both, since
                          Mark Reviewed IS the re-review action there. */}
                      {account.review_status !== 'reviewed' && (
                        <MarkReviewedButton
                          accountNumber={account.account_number}
                          days={days}
                          onReviewed={onReviewed}
                        />
                      )}
                      {account.review_status !== 'unreviewed' && (
                        <UndoReviewButton
                          accountNumber={account.account_number}
                          onReviewed={onReviewed}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export { AccountStatusCell };
export const sortAccounts = (accounts, sortConfig) =>
  sortByColumn(accounts, ACCOUNT_SORT_ACCESSORS, sortConfig) || accounts;

export default AccountsSubtable;
